'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useAuth } from '@/contexts/AuthContext'
import type { UserRole } from '@/types/auth'
import {
  readActiveRoleFromStorage,
  writeActiveRoleToStorage,
} from '@/lib/activeRoleStorage'
import { hasCapability } from '@/lib/auth/capabilities'
import { userHasRole } from '@/lib/auth/roles'
import {
  readRolePerspectiveFromStorage,
  writeRolePerspectiveToStorage,
} from '@/lib/rolePerspectiveStorage'

/** Roles a system admin can simulate in the UI (excludes their real role). */
export const ADMIN_PREVIEW_ROLES: readonly UserRole[] = [
  'Management',
  'Doctor',
  'Ophthalmologist',
  'Optometrist',
  'Nurse',
  'Dispensary',
  'Accountant',
  'Patient',
] as const

interface RolePerspectiveContextValue {
  effectiveRole: UserRole | undefined
  viewAsRole: UserRole | null
  setViewAsRole: (role: UserRole | null) => void
  /** When the signed-in user has multiple assigned roles, optional UI override (client-only). */
  actingRole: UserRole | null
  setActingRole: (role: UserRole | null) => void
  isRolePreviewActive: boolean
  isActingRoleOverride: boolean
}

const RolePerspectiveContext = createContext<RolePerspectiveContextValue | null>(
  null
)

export function RolePerspectiveProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { currentUser } = useAuth()
  const userId = currentUser?.id ?? null
  const [viewAsRole, setViewAsRoleState] = useState<UserRole | null>(
    readRolePerspectiveFromStorage
  )
  const [actingRoleState, setActingRoleState] = useState<UserRole | null>(
    readActiveRoleFromStorage
  )
  // Track which user the in-memory preview state belongs to. Previously AuthTree
  // remounted this provider on user id change; that tore down /login during soft
  // post-login navigation. Reset perspective state in place instead.
  const [boundUserId, setBoundUserId] = useState<string | null>(userId)
  if (userId !== boundUserId) {
    setBoundUserId(userId)
    setViewAsRoleState(userId ? readRolePerspectiveFromStorage() : null)
    setActingRoleState(userId ? readActiveRoleFromStorage() : null)
  }

  const actingRole = useMemo((): UserRole | null => {
    if (!currentUser || currentUser.roles.length <= 1) return null
    if (actingRoleState && userHasRole(currentUser.roles, actingRoleState)) {
      return actingRoleState
    }
    return null
  }, [currentUser, actingRoleState])

  const setViewAsRole = useCallback(
    (role: UserRole | null) => {
      // Use assigned roles (not effective), so SystemAdmin can always exit preview.
      if (!hasCapability(currentUser?.roles ?? [], 'platform.preview_role')) return
      setViewAsRoleState(role)
      writeRolePerspectiveToStorage(role)
    },
    [currentUser?.roles]
  )

  const setActingRole = useCallback(
    (role: UserRole | null) => {
      if (!currentUser || currentUser.roles.length <= 1) return
      if (role !== null && !userHasRole(currentUser.roles, role)) return
      writeActiveRoleToStorage(role)
      setActingRoleState(role)
    },
    [currentUser]
  )

  const effectiveRole = useMemo((): UserRole | undefined => {
    if (!currentUser) return undefined
    // Act-as must win over admin preview: preview is for exploring the UI, but "Act as" is the user's
    // explicit choice among roles they actually hold (e.g. SystemAdmin+Doctor saving a consultation).
    if (
      currentUser.roles.length > 1 &&
      actingRole !== null &&
      userHasRole(currentUser.roles, actingRole)
    ) {
      return actingRole
    }
    const canPreviewRole = hasCapability(currentUser.roles, 'platform.preview_role')
    if (canPreviewRole && viewAsRole !== null && viewAsRole !== 'SystemAdmin') {
      return viewAsRole
    }
    return currentUser.role
  }, [currentUser, viewAsRole, actingRole])

  const isRolePreviewActive = Boolean(
    currentUser &&
      hasCapability(currentUser.roles, 'platform.preview_role') &&
      viewAsRole !== null &&
      viewAsRole !== 'SystemAdmin'
  )

  // Any explicit act-as selection narrows the UI — including the primary role.
  // Only "Default" (null) keeps the multi-role union. Requiring actingRole !==
  // currentUser.role made picking the primary role a no-op until remount/refresh.
  const isActingRoleOverride = Boolean(
    currentUser && currentUser.roles.length > 1 && actingRole !== null
  )

  const value = useMemo(
    () => ({
      effectiveRole,
      viewAsRole:
        currentUser && hasCapability(currentUser.roles, 'platform.preview_role')
          ? viewAsRole
          : null,
      setViewAsRole,
      actingRole:
        currentUser && currentUser.roles.length > 1 ? actingRole : null,
      setActingRole,
      isRolePreviewActive,
      isActingRoleOverride,
    }),
    [
      effectiveRole,
      viewAsRole,
      setViewAsRole,
      actingRole,
      setActingRole,
      isRolePreviewActive,
      isActingRoleOverride,
      currentUser,
    ]
  )

  return (
    <RolePerspectiveContext.Provider value={value}>
      {children}
    </RolePerspectiveContext.Provider>
  )
}

export function useRolePerspective(): RolePerspectiveContextValue {
  const ctx = useContext(RolePerspectiveContext)
  if (!ctx) {
    throw new Error('useRolePerspective must be used within RolePerspectiveProvider')
  }
  return ctx
}

export function useOptionalRolePerspective(): RolePerspectiveContextValue | null {
  return useContext(RolePerspectiveContext)
}

export function useEffectiveRole(): UserRole | undefined {
  const ctx = useContext(RolePerspectiveContext)
  const { currentUser } = useAuth()
  if (!ctx) return currentUser?.role
  return ctx.effectiveRole
}
